import { Text } from '~/domains/design-system/Text';
import { SectionContent } from '~/domains/contest/domains/detail/components/Sections/components';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';

import useAdditionalPayoutCountdownWidget from './useAdditionalPayoutCountdownWidget';
import useStyles from './AdditionalPayoutCountdownWidget.styles';
import { useMantineTheme } from '@mantine/core';
import { useAdditionalPayoutSettings } from '../../hooks/useAdditionalPayoutSettings';
import useTranslation from 'next-translate/useTranslation';

type AdditionalPayoutCountdownWidgetProps = {
  contest: Contest | MyUserContest;
  isSidePanel?: boolean;
};

function AdditionalPayoutCountdownWidget({
  contest,
  isSidePanel,
}: AdditionalPayoutCountdownWidgetProps) {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { t } = useTranslation('contest');
  const {
    isEnabled: isAdditionalPayoutEnabled,
    deadline,
    hasPrizeIncreased,
    message,
  } = useAdditionalPayoutSettings(contest);
  const { timeComponents, hasDeadlinePassed, isEnabled } = useAdditionalPayoutCountdownWidget({
    isEnabled: isAdditionalPayoutEnabled,
    deadline,
  });

  if (!isEnabled || hasDeadlinePassed) {
    return null;
  }
  return (
    <SectionContent isSidePanel={isSidePanel}>
      <div className={classes.container}>
        <Text variant="subhead-medium" className={classes.title}>
          {t('common.additionalPayoutCountdownWidget.title', {
            status: hasPrizeIncreased
              ? t('common.additionalPayoutCountdownWidget.status.next')
              : t('common.additionalPayoutCountdownWidget.status.guaranteed'),
          })}
        </Text>

        <div className={classes.timeContainer}>
          {timeComponents.map(({ value, label, unit }) => (
            <div key={unit} className={classes.timeBox}>
              <Text variant="headline-xl" className={classes.timeValue}>
                {value}
              </Text>
              <Text variant="subhead-xx-small" className={classes.timeLabel}>
                {label}
              </Text>
            </div>
          ))}
        </div>

        {message && (
          <div className={classes.descriptionContainer}>
            <Text variant="subhead-xx-small" color={theme.colors.gray[6]}>
              {t('common.additionalPayoutCountdownWidget.subtitle')}
            </Text>
            <Text variant="title-small">{message}</Text>
          </div>
        )}
      </div>
    </SectionContent>
  );
}

export default AdditionalPayoutCountdownWidget;
